import React, { FC, useCallback, useState } from "react";
import classNames from "classnames";
import { Dropdown } from "react-bootstrap";
import { TransactionDateColumnContent } from "../../../transactions/ListItem/TransactionDateColumnContent";
import { TransactionLockColumnContent } from "../../../transactions/ListItem/TransactionLockColumnContent";
import { TransactionDescriptionContent } from "../../transactionContent/TransactionDescriptionContent";
import { TransactionCategory } from "../../../transactions/TransactionCategory";
import { TransactionAccountColumnContent } from "../../../transactions/ListItem/TransactionAccountColumnContent";
import { TransactionAmount } from "../../../transactions/TransactionAmount";
import { TransactionListItemDto } from "../../../../common/types/transaction";
import { EntityIcon } from "../../../entity/EntityIcon";
import { ReceiptUploadDropzone } from "../../../transactions/ReceiptUpload/ReceiptUploadDropzone";
import { GridTableRow } from "../../../general/Tables/GridTable/GridTable";
import { toBase64 } from "../../../../helpers/file";
import { uploadTransactionReceipt } from "../../../../lib/transactions";
import { FinancialDocument } from "../../../../common/types/financialDocument";
import { ManagementDropdown } from "../../../general/ManagementDropdown/ManagementDropdown";
import { DotsHorizontalIcon } from "../../../../icons";
import { CaptureClicks } from "../../../general/CaptureClicks";
import { useUpdateTransactionMutation } from "../../../../mutations/transaction";
import { useUploadReceiptsActionContext } from "../uploadReceiptsAction.context";
import { useIsDateLocked } from "../../../../hooks/useIsDateLocked";
import { ReceiptColumn } from "./ReceiptColumn";
import styles from "./TransactionsModal.module.scss";

interface TransactionRowProps {
    transaction: TransactionListItemDto;
}

export const TransactionRow: FC<TransactionRowProps> = ({ transaction }) => {
    const [uploadedDocument, setUploadedDocument] =
        useState<FinancialDocument | null>(null);
    const [uploading, setUploading] = useState(false);
    const updateTransaction = useUpdateTransactionMutation();
    const { refreshTransactions } = useUploadReceiptsActionContext();

    const addReceipt = useCallback(
        async (file: File) => {
            setUploading(true);

            try {
                const receipt = await toBase64(file);

                const uploadedReceipt = await uploadTransactionReceipt(
                    transaction.id,
                    {
                        receipt,
                        contentType: file.type,
                        name: file.name,
                    },
                );

                setUploadedDocument(uploadedReceipt);
            } finally {
                setUploading(false);
            }
        },
        [transaction.id],
    );

    const onSnoozeRequirements = useCallback(async () => {
        await updateTransaction.mutateAsync({
            transaction,
            update: {
                requirementsSnoozed: true,
            },
        });

        refreshTransactions();
    }, [refreshTransactions, transaction, updateTransaction]);

    const isLocked = useIsDateLocked(transaction.entity.id, transaction.date);

    return (
        <GridTableRow className={classNames(styles.row)}>
            <div>
                <TransactionLockColumnContent isLocked={isLocked} />
            </div>
            <div>
                <TransactionDateColumnContent transaction={transaction} />
            </div>
            <TransactionDescriptionContent transaction={transaction} />

            <div>
                <TransactionCategory transaction={transaction} />
            </div>

            <TransactionAccountColumnContent
                transaction={transaction}
                size="xs"
            />
            <TransactionAmount
                className={classNames(styles.amountColumn, styles.amountValue)}
                transaction={transaction}
            />
            <div>
                <EntityIcon entity={transaction.entity} size="xs" />
            </div>

            <ReceiptUploadDropzone
                onAddReceipt={addReceipt}
                containerClassName={styles.receiptColumn}
            >
                <ReceiptColumn
                    uploading={uploading}
                    receipt={uploadedDocument}
                    transaction={transaction}
                />
            </ReceiptUploadDropzone>

            <div>
                <CaptureClicks>
                    <ManagementDropdown
                        trigger={<DotsHorizontalIcon />}
                        id={`transaction-dropdown-${transaction.id}`}
                        popperConfigStrategy="fixed"
                    >
                        <Dropdown.Item
                            as="button"
                            onClick={onSnoozeRequirements}
                        >
                            <div className={styles.dropdownItem}>
                                <header>Snooze requirements</header>
                                <p>
                                    This means you may not have the
                                    substantiation required by the IRS during an
                                    audit.
                                </p>
                            </div>
                        </Dropdown.Item>
                    </ManagementDropdown>
                </CaptureClicks>
            </div>
        </GridTableRow>
    );
};
